import { motion, useReducedMotion } from 'motion/react'
import { ArrowUpRight, Clock3, Languages, Mail, Video } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { creator } from '@/config/creator'
import SocialLinks from './SocialLinks'

const details = [
  { icon: Video, label: 'Google Meet', text: 'A conversation, wherever you are.' },
  { icon: Clock3, label: '15 focused minutes', text: 'One idea. A clear next step.' },
  { icon: Languages, label: 'English / Italian', text: 'Choose the language you feel at home in.' },
]

export default function MeetingSection() {
  const reduceMotion = useReducedMotion()

  return (
    <section id="contact" aria-labelledby="contact-heading" className="creator-section">
      <div id="meet" className="creator-container scroll-mt-28">
        <motion.div initial={reduceMotion ? false : { opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: reduceMotion ? 0 : 0.5 }} className="grid min-w-0 grid-cols-1 gap-5 lg:grid-cols-12">
          <div className="bento-card bg-accent p-7 text-foreground sm:p-10 lg:col-span-8">
            <p className="creator-label">An open invitation</p>
            <h2 id="contact-heading" className="creator-display mt-5 text-5xl sm:text-6xl lg:text-7xl">Let’s build<br />something.</h2>
            <p className="mt-6 max-w-xl text-lg leading-8">Have an idea, a question about Coderblock, or a different perspective on what comes next? I’d love to hear it.</p>
            <p className="mt-3 max-w-xl text-base leading-7">Zero slides. No pitch deck required. Just a focused conversation about what you’re building and where I can help.</p>
            <a href={creator.meetingUrl} target="_blank" rel="noopener noreferrer" className="creator-button mt-7 motion-reduce:transform-none">Book 15 minutes with me <ArrowUpRight size={18} aria-hidden="true" /></a>
            <ul className="mt-9 grid grid-cols-1 gap-5 border-t border-foreground/20 pt-7 sm:grid-cols-3">
              {details.map(({ icon: Icon, label, text }) => (
                <li key={label} className="min-w-0">
                  <Icon size={22} aria-hidden="true" />
                  <h3 className="mt-3 text-sm font-bold">{label}</h3>
                  <p className="mt-1 text-sm leading-6">{text}</p>
                </li>
              ))}
            </ul>
          </div>
          <div className="grid min-w-0 gap-5 lg:col-span-4">
            <div className="bento-card flex flex-col items-center justify-center bg-card p-7 text-center sm:p-8">
              <p className="creator-label text-muted-foreground">Take the conversation with you</p>
              <a href={creator.meetingUrl} target="_blank" rel="noopener noreferrer" aria-label="Open my meeting booking page" className="mt-5 inline-flex max-w-full rounded-2xl border border-border bg-card p-4">
                <QRCodeSVG value={creator.meetingUrl} size={180} level="M" bgColor="hsl(0, 0%, 100%)" fgColor="hsl(var(--foreground))" title="Scan to book a 15-minute meeting with Danilo" className="h-auto max-w-full" />
              </a>
              <p className="mt-4 max-w-xs text-sm leading-6 text-muted-foreground">Scan to choose a time, or use the booking button. We’ll meet on Google Meet.</p>
            </div>
            <div className="bento-card bg-secondary p-6 sm:p-8">
              <h3 className="font-display text-2xl font-bold tracking-tight">Keep in touch.</h3>
              <p className="mb-4 mt-2 text-sm leading-6">I share the ideas, experiments and lessons along the way.</p>
              <SocialLinks compact limit={3} />
              {creator.email?.trim() && (
                <a href={`mailto:${creator.email.trim()}`} className="mt-4 inline-flex min-h-11 max-w-full items-center gap-2 text-sm font-semibold text-foreground">
                  <Mail size={18} aria-hidden="true" className="shrink-0" />
                  <span className="break-all">{creator.email.trim()}</span>
                </a>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
